<template>
  <Loader :value="isNew ? promise : promises.fetchOne" :message="$metaInfo.title" class="form-create">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #actions>
          <ButtonComponent
            v-if="!isNew"
            tag="router-link"
            :to="{ name: 'form-preview', params: { id: id } }"
            target="_blank"
            :label="$t('preview')"
            :icon="['fal', 'eye']"
          />
          <Actions placement="form" :query="{ form: id }">
            <template #bottom>
              <ButtonComponent
                v-if="!isNew && $store.getters.hasAccess('admin')"
                :label="$t('delete')"
                @click="removeDialog = true"
                :icon="['fal', 'times']"
              />
            </template>
          </Actions>

          <ButtonComponent
            :label="isNew ? $t('create') : $t('save')"
            theme="primary"
            type="submit"
            :class="{ 'elder-button--disabled': !validator.passes || !hasChanges }"
            :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
            :promise="promises.mutate"
            v-tooltip="{ content: hasChanges ? null : $t('noUnsavedChanges') }"
            @click="submit"
          />
        </template>
      </HeaderComponent>

      <Card tag="form" @submit.prevent="submit" class="form-create__form">
        <template #header>
          <FontAwesomeIcon :icon="['fal', 'square-list']" size="6x" />
        </template>
        <template #default>
          <FormBuilder
            :labels="labels"
            v-model="data"
            :validator="validator"
            :schema="customFieldSchemas"
            v-if="isNew ? data : data"
          ></FormBuilder>
        </template>
      </Card>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            ...referenceRouteInfo,
          })
        "
        :resource="$tc('form')"
      />
    </div>
  </Loader>
</template>

<script>
import ValidatorMixin from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import HeaderComponent from '@/components/Header'
import FormBuilder from '@/components/Form/Builder'
import Actions from '@/components/Actions'
import RemoveComponent from '@/components/Remove'

import Card from '@/components/Card'
import { mapState, mapActions, mapMutations } from 'vuex'
import Model from '@/models/Form'
import { buildStepsFields } from '@/utils'
import fields from '@/components/Form/fields.js'
import { getValidatorConfig } from '@kvass/custom-fields'
import ProjectAPI from '@/api/Project'
import PageAPI from '@/api/Page'
import { path } from 'ramda'

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'item',
      guard: true,
      model: Model,

      transform: {
        receivers: v => {
          if (!path(['0', 'user'], v) || !v.length) return [{ user: '' }]
          return v
        },
      },
    }),
  ],
  props: {
    reference: String,
    id: String,
  },
  data() {
    return {
      referenceName: null,
      removeDialog: false,
      promise: null,
      api: {
        Project: ProjectAPI,
        Page: PageAPI,
      },
    }
  },
  watch: {
    id: {
      handler(id = '') {
        if (!id) {
          this.set(null)
          return this.fetchReference()
        }

        this.fetch({ id }).then(() => {
          this.data.receivers = this.data.receivers.length ? this.data.receivers : [{ user: '' }]
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Form', ['item', 'promises']),
    customFieldSchemas() {
      return buildStepsFields(Array.from(fields.values()), [], this).default
    },
    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}

      //transform model keys from customfields
      return Object.fromEntries(
        Object.entries(getValidatorConfig(this.customFieldSchemas, this.data)).map(([key, value]) => {
          return [
            key,
            Object.fromEntries(Object.entries(value).map(([fieldKey, fieldValue]) => [fieldKey, fieldValue])),
          ]
        }),
      )
    },
    rules() {
      return {
        title: 'required',
        ...this.customFieldValidatorConfig.rules,
      }
    },
    labels() {
      return {
        title: this.$t('title'),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    isNew() {
      return !this.id
    },

    onModel() {
      return this.$route.meta.onModel || 'Project'
    },

    breadcrumbs() {
      const context = this.onModel.toLowerCase()

      return [
        {
          title: (JSON.parse(sessionStorage.getItem(`${context}:category`)) || {}).label || this.$tc(context, 2),
          action: { name: `${context}s` },
        },

        {
          title: this.referenceName || this.$path('item.reference.name'),
          action: this.referenceRouteInfo,
        },

        { title: this.$metaInfo.title },
      ]
    },
    referenceRouteInfo() {
      const context = this.onModel.toLowerCase()
      return {
        name: `${context}-dashboard`,
        params: { id: this.reference || this.$path('item.reference.id') },
      }
    },

    fullReference() {
      return {
        ref: this.reference,
        onModel: this.onModel,
      }
    },
  },
  methods: {
    ...mapActions('Form', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Form', ['set']),
    submit() {
      if (!this.hasChanges || !this.validator.passes) return Promise.resolve()

      const data = {
        ...this.getData({ diff: true }),
        reference: this.fullReference,
      }

      // drop empty placement rows
      if (data.placement) data.placement = data.placement.filter(Boolean)

      return this.mutate({ ...data, id: this.id }).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: `${this.onModel.toLowerCase()}-form-edit`,
          params: { id: data.id, reference: this.reference },
        })
      })
    },
    fetchReference() {
      if (!this.onModel) return
      this.data = null
      this.promise = this.api[this.onModel]
        .get({
          id: this.reference,
        })
        .then(data => {
          this.referenceName = data.name
        })
    },
  },

  created() {},
  metaInfo() {
    return {
      title: this.isNew ? `${this.$t('create')} ${this.$t('form').toLowerCase()}` : this.$path('item.title'),
    }
  },
  components: {
    HeaderComponent,
    Card,
    FormBuilder,
    Actions,
    RemoveComponent,
  },
}
</script>

<style lang="scss">
.form-create {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 1200px;
  }
}
</style>
